import type React from 'react';
import { Box, Paragraph, VisuallyHidden } from '@zillow/constellation-10';
import { FULLSTORY_EXCLUDE_CLASS_NAME } from '../../constants';
import { parseMessageText } from '../../utils/parseMessageText';
import { chatBubbleStyles } from '../ChatBubble/styles';
import { CHAT_BUBBLE_VARIANT_STYLES, type ChatBubbleVariant, getScreenReaderSenderName } from '../ChatBubble';

export type InteractiveChatBubbleProps = {
  children: React.ReactNode;
  senderName?: string;
  shouldIncludeGutter?: boolean;
  variant: ChatBubbleVariant;
  onLinkClick?: (url: string, displayText?: string) => void;
  showLinkRemovedPopover?: boolean;
};

export default function InteractiveChatBubble({
  children,
  senderName,
  shouldIncludeGutter = false,
  variant,
  onLinkClick,
  showLinkRemovedPopover = false,
}: InteractiveChatBubbleProps) {
  const variantStyles = CHAT_BUBBLE_VARIANT_STYLES[variant];
  const isMessageOwner = variant === 'messageOwner';
  const processedChildren =
    typeof children === 'string'
      ? parseMessageText(children, isMessageOwner, onLinkClick, showLinkRemovedPopover)
      : children;
  const screenReaderSenderName = getScreenReaderSenderName(variant, senderName);
  const textColor = isMessageOwner ? 'text.onImpact.neutral-fixed' : 'text.neutral';

  return (
    <Box
      data-testid="interactive-chat-bubble"
      css={{
        ...chatBubbleStyles,
        ...variantStyles,
        ...(shouldIncludeGutter && {
          marginLeft: isMessageOwner ? 'layout.looser' : '0',
          marginRight: isMessageOwner ? '0' : 'layout.looser',
        }),
      }}
    >
      {!!screenReaderSenderName && (
        <VisuallyHidden className={FULLSTORY_EXCLUDE_CLASS_NAME}>{screenReaderSenderName}</VisuallyHidden>
      )}
      <Paragraph css={{ color: textColor, wordBreak: 'break-word' }}>{processedChildren}</Paragraph>
    </Box>
  );
}
